import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { History as HistoryIcon, Calendar, Briefcase, Database, Trash2, X, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { UploadMetadata } from '../types';
import toast from 'react-hot-toast';

const DeleteModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  upload 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onConfirm: () => void; 
  upload: UploadMetadata | null;
}) => {
  if (!isOpen || !upload) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <h3 className="text-lg font-medium text-gray-900">Confirm Deletion</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            Are you sure you want to delete this upload? This action cannot be undone.
          </p>
          <div className="bg-gray-50 p-3 rounded-md">
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
              <Briefcase className="h-4 w-4" />
              <span>{upload.fileName}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="h-4 w-4" />
              <span>Date: {format(new Date(upload.dateCollecte || upload.dateUpload), 'PP')}</span>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-md flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete Upload
          </button>
        </div>
      </div>
    </div>
  );
};

const History = () => {
  const { currentUser } = useAuth();
  const [uploads, setUploads] = useState<UploadMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    upload: UploadMetadata | null;
  }>({
    isOpen: false,
    upload: null
  });

  const fetchUploads = async () => {
    if (!currentUser) return;

    try {
      const uploadsQuery = query(
        collection(db, 'uploads'),
        where('userId', '==', currentUser.uid),
        orderBy('dateUpload', 'desc')
      );

      const querySnapshot = await getDocs(uploadsQuery);
      const uploadsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as UploadMetadata));

      setUploads(uploadsData);
    } catch (error) {
      console.error('Error fetching uploads:', error);
      toast.error('Failed to fetch uploads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUploads();
  }, [currentUser]);

  const handleDelete = async (upload: UploadMetadata) => {
    setDeleteModal({ isOpen: true, upload });
  };

  const confirmDelete = async () => {
    if (!deleteModal.upload) return;

    setDeleting(deleteModal.upload.id);
    try {
      await deleteDoc(doc(db, 'uploads', deleteModal.upload.id));
      toast.success('Upload deleted successfully');
      await fetchUploads(); // Refresh the list
    } catch (error) {
      console.error('Error deleting upload:', error);
      toast.error('Failed to delete upload');
    } finally {
      setDeleting(null);
      setDeleteModal({ isOpen: false, upload: null });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, upload: null })}
        onConfirm={confirmDelete}
        upload={deleteModal.upload}
      />

      <div className="bg-white p-8 rounded-lg shadow-md">
        <div className="flex items-center gap-3 mb-8">
          <HistoryIcon className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-800">Upload History</h1>
        </div>

        {uploads.length === 0 ? (
          <div className="text-center py-12">
            <Database className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No uploads yet</h3>
            <p className="text-gray-500 mb-4">Start by uploading your first business data file</p>
            <Link
              to="/upload"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              <HistoryIcon className="h-5 w-5" />
              Upload Data
            </Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {uploads.map((upload) => (
              <div
                key={upload.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-800 mb-2">
                      {upload.fileName}
                    </h3>
                    <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4" />
                <span>
                  Date : {format(new Date(upload.dateCollecte || upload.dateUpload), 'PPP', { locale: fr })}
                </span>
              </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/compare?id=${upload.id}`}
                      className="bg-blue-50 text-blue-600 px-4 py-2 rounded-md hover:bg-blue-100 transition-colors"
                    >
                      Compare
                    </Link>
                    <button
                      onClick={() => handleDelete(upload)}
                      disabled={deleting === upload.id}
                      className="text-red-600 hover:text-red-700 p-2 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
                      title="Delete upload"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center text-sm text-gray-500 pt-4 border-t">
                  <span>
                    Mis en ligne le {format(new Date(upload.dateUpload), 'PPPp', { locale: fr })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
